<script setup lang="ts">
interface FilterOption {
  key?: string
  label?: string
  active?: boolean
  children?: FilterOption[]
  [key: string]: unknown
}

interface FilterItem {
  key: string
  label: string
  type?: string
  options: FilterOption[]
  activeOptionsCount?: number
  isExpanded?: boolean
  isShowSearchBox?: boolean
  limitOptionsShowSearchBox?: number
  [key: string]: unknown
}

const { t: $t } = useI18n()

const props = defineProps<{
  filter: FilterItem
}>()

const meta = inject('meta', ref())

const {
  optionToggle,
  filterApply,
  filterRemove,
  filtersApplyAll,
  getExpectedTotal,
} = useCatalogFilters(meta)

const isCategoryFilter = computed<boolean>(() => {
  return ['cat', 'category'].includes(props.filter.key)
})

const isExpanded = computed<boolean>(() => {
  return !!props.filter.isExpanded || isCategoryFilter.value
})

const debounceGetExpectedTotal = useDebounceFn(() => {
  getExpectedTotal()
}, 150)

function toggle(filterKey: string, optionKey: string) {
  optionToggle(filterKey, optionKey)
  getExpectedTotal()
}

function filterApplyAndUpdate(filterKey: string) {
  filterApply(filterKey)
  filtersApplyAll()
}

function filterResetAndUpdate(filterKey: string) {
  filterRemove(filterKey) // Empty all active option in this filter
  getExpectedTotal()
  filtersApplyAll()
}
</script>

<template>
  <UiCollapsable
    class="filter-item"
    :active="filter.activeOptionsCount"
    :reference="filter.key"
    :label="$t(filter.label)"
    :open="isExpanded"
  >
    <template #label="{ open }">
      <span>{{ $t(filter.label) }}</span>

      <template v-if="!isCategoryFilter">
        <span
          v-if="filter.activeOptionsCount"
          class="filter-item__count"
        >{{ filter.activeOptionsCount }}</span>

        <Icon
          v-if="open"
          name="icon:min"
          class="icon"
        />
        <Icon
          v-else
          name="icon:plus"
          class="icon"
        />
      </template>
    </template>

    <LazyFilterLinks
      v-if="isCategoryFilter"
      :filter="filter"
    />
    <LazyFilterRange
      v-else-if="filter.type === 'range'"
      :filter="filter"
      @filter-reset="filterResetAndUpdate(filter.key)"
      @filter-apply="filterApplyAndUpdate(filter.key)"
      @input="debounceGetExpectedTotal"
    />
    <LazyFilterOptions
      v-else-if="filter.type === 'default' || filter.type === 'visual'"
      :filter="filter"
      @option-toggle="(optionKey: string) => toggle(filter.key, optionKey)"
      @filter-reset="filterResetAndUpdate(filter.key)"
      @filter-apply="filterApplyAndUpdate(filter.key)"
    />
    <dev-only v-else>
      <pre>{{ filter }}</pre>
    </dev-only>
  </UiCollapsable>
</template>

<style>
@import url("~layers/app/components/Filter/Item.css");
</style>
